import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Route } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TypeOptions } from "@/components/VehicleTypes";
import { SERVICE, TRIP_STATUS, PAY_STATUS, fmtDateTime } from "@/lib/util";
import { Loader, Pill, Money, VehNo, payColor, tripColor } from "@/components/common";
import { PageTitle, SearchBox, Table, Td, useDebounced } from "@/pages/admin/ui";
import DateInput from "@/components/DateInput";

export const DateRange = ({ from, to, setFrom, setTo }) => (
  <div className="flex gap-2 items-center">
    <DateInput testid="date-from" className="field field-sm w-44" placeholder="من تاريخ" value={from} onChange={(e) => setFrom(e.target.value)} />
    <span className="text-slate-400">إلى</span>
    <DateInput testid="date-to" className="field field-sm w-44" placeholder="إلى تاريخ" value={to} onChange={(e) => setTo(e.target.value)} />
  </div>
);

export const Pager = ({ page, total, size = 50, setPage }) => total > size && (
  <div className="flex gap-2 justify-center mt-4">
    <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="btn btn-sm btn-ghost">السابق</button>
    <span className="px-3 py-1">{page} / {Math.ceil(total / size)}</span>
    <button disabled={page * size >= total} onClick={() => setPage(page + 1)} className="btn btn-sm btn-ghost">التالي</button>
  </div>
);

export default function Trips() {
  const [sp] = useSearchParams();
  const [q, setQ] = useState("");
  const dq = useDebounced(q);
  const [f, setF] = useState({ status: sp.get("status") || "", service_type: "", date_from: sp.get("date_from") || "", date_to: sp.get("date_to") || "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/trips", { params: { q: dq, ...f, page } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [dq, f, page]);
  const set = (k) => (v) => { setPage(1); setF({ ...f, [k]: v }); };
  return (
    <div>
      <PageTitle title="النقلات" sub={data ? `${data.total} نقلة` : ""} icon={Route} color="text-blue-700" />
      <div className="flex flex-wrap gap-3 mb-4">
        <SearchBox value={q} onChange={(v) => { setPage(1); setQ(v); }} placeholder="رقم النقلة، لوحة السيارة المنقولة، رقم مركبة الشركة (إداري/عادي)، هاتف، سائق" testid="trips-search" />
        <select data-testid="trips-status-filter" className="field field-sm w-36" value={f.status} onChange={(e) => set("status")(e.target.value)}>
          <option value="">كل الحالات</option>{Object.entries(TRIP_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <select data-testid="trips-type-filter" className="field field-sm w-36" value={f.service_type} onChange={(e) => set("service_type")(e.target.value)}>
          <option value="">كل الخدمات</option><TypeOptions /></select>
        <DateRange from={f.date_from} to={f.date_to} setFrom={set("date_from")} setTo={set("date_to")} />
      </div>
      {!data ? <Loader /> : (
        <Table head={["رقم النقلة", "التاريخ", "الخدمة", "السائق", "مركبة النقل", "المنقول", "المسار", "السعر", "الدفع", "الحالة"]} testid="trips-table">
          {data.items.map((t) => <tr key={t.id} className="hover:bg-slate-50">
            <Td><Link data-testid={`trip-row-${t.number}`} to={`/admin/trips/${t.id}`} className="ltr text-blue-700 font-black">{t.number}</Link></Td>
            <Td className="whitespace-nowrap">{fmtDateTime(t.created_at)}</Td><Td>{SERVICE[t.service_type]}</Td>
            <Td>{t.driver_name} <span className="ltr text-xs text-slate-500">{t.driver_serial}</span></Td><Td><VehNo adm={t.vehicle_admin_number} plate={t.vehicle_plate} /></Td>
            <Td><span className="ltr">{t.car_plate || t.goods_type || "—"}</span></Td>
            <Td><bdi>{t.pickup}</bdi> ← <bdi>{t.dropoff}</bdi>{t.destination_changes ? <Pill color="amber">غُيرت الوجهة</Pill> : null}</Td>
            <Td><Money fils={t.price_fils} /></Td><Td><Pill color={payColor(t.payment_status)}>{PAY_STATUS[t.payment_status]}</Pill></Td>
            <Td><Pill color={tripColor(t.status)}>{TRIP_STATUS[t.status]}</Pill></Td>
          </tr>)}
        </Table>
      )}
      {data && <Pager page={page} total={data.total} setPage={setPage} />}
    </div>
  );
}
